import { useEffect, useRef } from "react";
import useReducedMotion from "../hooks/useReducedMotion";

/**
 * Ambient backdrop: orbs, grain, vignette, optional cursor glow,
 * and a scroll-progress bar. Pointer + scroll work is rAF-throttled
 * and avoids React re-renders on every frame.
 */
export default function Ambient() {
  const glowRef = useRef(null);
  const scrollBarRef = useRef(null);
  const reducedMotion = useReducedMotion();
  const pointer = useRef({ x: 0, y: 0, dirty: false });
  const rafId = useRef(0);

  useEffect(() => {
    const glow = glowRef.current;
    const bar = scrollBarRef.current;

    const flush = () => {
      rafId.current = 0;
      if (bar) {
        const doc = document.documentElement;
        const max = doc.scrollHeight - doc.clientHeight;
        const pct = max > 0 ? (doc.scrollTop / max) * 100 : 0;
        bar.style.width = `${pct}%`;
      }
      if (glow && pointer.current.dirty) {
        pointer.current.dirty = false;
        glow.style.transform = `translate(calc(${pointer.current.x}px - 50%), calc(${pointer.current.y}px - 50%))`;
      }
    };

    const schedule = () => {
      if (!rafId.current) rafId.current = requestAnimationFrame(flush);
    };

    const onPointerMove = (e) => {
      if (!glow || reducedMotion) return;
      pointer.current.x = e.clientX;
      pointer.current.y = e.clientY;
      pointer.current.dirty = true;
      schedule();
    };

    const onScroll = () => schedule();

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    schedule();

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("scroll", onScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [reducedMotion]);

  return (
    <>
      <div className="scroll-bar" ref={scrollBarRef} />
      <div className={`ambient${reducedMotion ? " reduced" : ""}`} aria-hidden="true">
        <div className="orb a" />
        <div className="orb b" />
        <div className="orb c" />
        <div className="noise" />
        <div className="vignette" />
      </div>
      {!reducedMotion && (
        <div className="cursor-glow" ref={glowRef} aria-hidden="true" />
      )}
    </>
  );
}
